import { useAtomValue } from 'jotai'
import { match } from 'ts-pattern'

import {
  SequenceMenu,
  SequenceMenuDivider,
  SequenceMenuItemType
} from '@sequencehq/core-components'

import {
  invoiceIdAtom,
  invoicePaymentStatusAtom,
  invoiceStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import {
  MenuItemConvertToDraft,
  MenuItemCreateCreditNote,
  MenuItemDeleteInvoice,
  MenuItemFinalizeInvoice,
  MenuItemIssueCreditNote,
  MenuItemRecordPayment,
  MenuItemSendTestInvoice,
  MenuItemViewPaymentDetails,
  MenuItemVoidInvoice
} from './actions'
import { usePermissions } from 'lib/rbac'
import { DuplicateInvoice, MarkAsSent } from 'Invoices/components/menuItems'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { EligibilityVerdict } from './eligibility'

type InvoiceStatus = Dashboardv99990101Api.Invoices.InvoiceStatus
type InvoicePaymentStatus = Dashboardv99990101Api.Invoices.InvoicePaymentStatus

// The CN V2 (issue-and-apply) flow adjusts the invoice's outstanding amount,
// so it only makes sense while something is still owed. Paid and uncollectible
// invoices instead open the drawer locked to "Refund manually". PARTIALLY_PAID
// is also excluded until stacked CN applications correctly reduce the balance.
const ADJUSTABLE_PAYMENT_STATUSES: InvoicePaymentStatus[] = [
  'UNPAID',
  'PARTIALLY_PAID'
]

export const KebabMenu = ({
  cnV2Eligibility,
  manualPaymentEligibility
}: {
  cnV2Eligibility: EligibilityVerdict
  manualPaymentEligibility: EligibilityVerdict
}) => {
  const status = useAtomValue(invoiceStatusAtom)
  const paymentStatus = useAtomValue(invoicePaymentStatusAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const { can } = usePermissions()

  if (!status) return null
  if (!invoiceId) return null

  const canAdjustAmountDue = Boolean(
    paymentStatus && ADJUSTABLE_PAYMENT_STATUSES.includes(paymentStatus)
  )

  const creditNoteMenuItem = match(cnV2Eligibility)
    .with('eligible', () => (
      <MenuItemIssueCreditNote
        key="issue-credit-note"
        invoiceId={invoiceId}
        lockToRefund={!canAdjustAmountDue}
      />
    ))
    .with('ineligible', () => (
      <MenuItemCreateCreditNote key="create-credit-note" />
    ))
    .with('pending', () => null)
    .exhaustive()

  // A fully paid invoice has nothing left to collect, so the apply-payment
  // action is hidden (the item's permission gating lives in the component).
  const canApplyPayment =
    manualPaymentEligibility === 'eligible' && paymentStatus !== 'PAID'
  const recordPaymentMenuItem = canApplyPayment ? (
    <MenuItemRecordPayment key="record-payment" invoiceId={invoiceId} />
  ) : null

  const items = match<InvoiceStatus, SequenceMenuItemType[]>(status)
    .with('DRAFT', () => {
      return [
        <MenuItemViewPaymentDetails key="view-payment-details" />,
        <MenuItemSendTestInvoice key="send-test-invoice" />,
        can('org.invoice:create') || can('org.invoice:finalize') ? (
          <SequenceMenuDivider key="divider-1" />
        ) : null,
        <MenuItemFinalizeInvoice key="finalize-invoice" />,
        <DuplicateInvoice key="duplicate-invoice" invoiceId={invoiceId} />,
        can('org.invoice:update') ? (
          <SequenceMenuDivider key="divider-2" />
        ) : null,
        <MarkAsSent
          key="mark-as-sent"
          invoiceId={invoiceId}
          invoiceNumber={undefined}
        />,
        can('org.invoice:update') || can('org.invoice:delete') ? (
          <SequenceMenuDivider key="divider-3" />
        ) : null,
        <MenuItemVoidInvoice key="void-invoice" />,
        <MenuItemDeleteInvoice key="delete-invoice" />
      ].filter(Boolean)
    })
    .with('IN_PROGRESS', () => {
      return [
        <MenuItemViewPaymentDetails key="view-payment-details" />,
        can('org.invoice:update') || can('org.invoice:create') ? (
          <SequenceMenuDivider key="divider-1" />
        ) : null,
        <MenuItemConvertToDraft key="convert-to-draft" />,
        <DuplicateInvoice key="duplicate-invoice" invoiceId={invoiceId} />,
        can('org.invoice:delete') ? (
          <SequenceMenuDivider key="divider-2" />
        ) : null,
        <MenuItemDeleteInvoice key="delete-invoice" />
      ].filter(Boolean)
    })
    .with('FINAL', () => {
      return [
        recordPaymentMenuItem,
        <MenuItemViewPaymentDetails key="view-payment-details" />,
        <MenuItemSendTestInvoice key="send-test-invoice" />,
        can('org.invoice:update') || can('org.creditNote:create') ? (
          <SequenceMenuDivider key="divider-1" />
        ) : null,
        creditNoteMenuItem,
        <DuplicateInvoice key="duplicate-invoice" invoiceId={invoiceId} />,
        <MarkAsSent
          key="mark-as-sent"
          invoiceId={invoiceId}
          invoiceNumber={undefined}
        />,
        can('org.invoice:update') ? (
          <SequenceMenuDivider key="divider-2" />
        ) : null,
        <MenuItemVoidInvoice key="void-invoice" />
      ].filter(Boolean)
    })
    .with('SENT', () => {
      return [
        recordPaymentMenuItem,
        <MenuItemViewPaymentDetails key="view-payment-details" />,
        can('org.invoice:create') || can('org.creditNote:create') ? (
          <SequenceMenuDivider key="divider-1" />
        ) : null,
        creditNoteMenuItem,
        invoiceId ? (
          <DuplicateInvoice key="duplicate-invoice" invoiceId={invoiceId} />
        ) : null,
        can('org.invoice:update') ? (
          <SequenceMenuDivider key="divider-2" />
        ) : null,
        <MenuItemVoidInvoice key="void-invoice" />
      ].filter(Boolean)
    })
    .with('VOIDED', () => {
      return [<MenuItemViewPaymentDetails key="view-payment-details" />]
    })
    .exhaustive()

  return (
    <SequenceMenu
      data-testid="header.kebab-menu"
      variant="ghost"
      buttonSize="medium"
      alignment="bottom-right"
      items={items}
    />
  )
}
